import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, ShieldAlert, Download } from "lucide-react";

export default function ValidatedDemo() {
  const [demo, setDemo] = useState(null);
  useEffect(() => { api.get("/validated-demo").then(({ data }) => setDemo(data)); }, []);
  if (!demo) return null;

  const stressPct = Math.min(100, (demo.calculated_max_stress_mpa / demo.referenced_yield_strength_mpa) * 100);

  return (
    <div className="space-y-10">
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span className="tag tag-green"><span className="led-pass mr-2 align-middle" />Validated</span>
          <span className="tag tag-gold">Demonstration</span>
        </div>
        <h1 className="text-3xl lg:text-4xl font-semibold text-white">Mechanical Mounting Bracket</h1>
        <p className="text-slate-400 mt-3 max-w-3xl text-sm">
          A single verified engineering result set produced by A.I.U.D.E.'s solver pipeline during development.
          All values below are from the actual solver run; anything not disclosed is marked explicitly.
          These results are shown for demonstration and status tracking — professional engineering review is required
          before any real-world fabrication or safety-critical use.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {[
          ["Solver", demo.solver, "font-mono"],
          ["Solver exit code", `${demo.solver_exit_code} · PASS`, "font-mono text-[#22C55E]"],
          ["Available exports", demo.export_types.join(" · "), "font-mono"],
        ].map(([k, v, cls]) => (
          <div key={k} className="panel-elev p-6 tick-brackets" data-testid={`demo-meta-${k.split(' ')[0].toLowerCase()}`}>
            <div className="metric-label">{k}</div>
            <div className={`mt-2 text-2xl text-white ${cls}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="panel-elev p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="font-tech">Stress vs. Yield</div>
            <span className="tag tag-blue">Yield-to-Stress Ratio</span>
          </div>
          <div className="relative h-14 border border-[#1E2F48] bg-[#0B111C]">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0066FF] to-[#00D2FF]" style={{ width: `${stressPct}%` }} />
            <div className="absolute inset-y-0 right-0 border-l-2 border-dashed border-[#C9A961]" />
            <div className="absolute top-1 left-3 font-mono text-[10px] text-white">σ_max = {demo.calculated_max_stress_mpa.toFixed(2)} MPa</div>
            <div className="absolute bottom-1 right-3 font-mono text-[10px] text-[#C9A961]">σ_yield = {demo.referenced_yield_strength_mpa} MPa</div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div>
              <div className="metric-label">Max stress</div>
              <div className="metric-value" data-testid="demo-max-stress">{demo.calculated_max_stress_mpa.toFixed(4)}</div>
              <div className="font-mono text-xs text-slate-500 mt-1">MPa</div>
            </div>
            <div>
              <div className="metric-label">Yield strength</div>
              <div className="metric-value">{demo.referenced_yield_strength_mpa}</div>
              <div className="font-mono text-xs text-slate-500 mt-1">MPa (referenced)</div>
            </div>
            <div>
              <div className="metric-label">Ratio σ_yield / σ_max</div>
              <div className="metric-value text-[#C9A961]" data-testid="demo-ratio">{demo.yield_to_stress_ratio}×</div>
              <div className="font-mono text-xs text-slate-500 mt-1">referenced yield strength ÷ calculated maximum stress — not a certified safety factor</div>
            </div>
          </div>
        </div>

        <div className="panel-elev p-6 space-y-4">
          <div className="font-tech">Displacement</div>
          <div>
            <div className="metric-value" data-testid="demo-displacement">{demo.max_displacement_mm.toFixed(4)}</div>
            <div className="font-mono text-xs text-slate-500 mt-1">mm (maximum)</div>
          </div>
          <div className="dim-line" />
          <div className="font-tech">Solver Completion</div>
          <div className="flex items-center gap-3">
            <span className="led-pass" />
            <div className="font-mono text-sm text-white">exit_code = 0</div>
          </div>
          <div className="text-slate-500 text-xs">CalculiX terminated normally.</div>
        </div>
      </div>

      <div className="panel-elev p-6">
        <div className="font-tech mb-3">Not Disclosed in This Demonstration</div>
        <div className="grid md:grid-cols-2 gap-3 text-sm text-slate-300">
          {Object.entries(demo.notes).map(([k, v]) => (
            <div key={k} className="panel p-3 flex justify-between">
              <span className="font-mono text-xs text-slate-400 capitalize">{k.replace(/_/g, " ")}</span>
              <span className="font-mono text-xs text-slate-500">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="panel-elev p-6 flex flex-wrap items-center gap-4 border-[#C9A961]/40">
        <ShieldAlert className="w-5 h-5 text-[#C9A961]" />
        <p className="text-slate-200 text-sm max-w-3xl">
          <strong className="text-[#C9A961]">Engineering review required.</strong> {demo.disclaimer}
        </p>
      </div>
    </div>
  );
}
